/**
 * product.js — PDP web components
 *
 * Components:
 *  ProductMain      — variant selection, AJAX ATC, availability, price, SKU
 *  ProductGallery   — image rail, swipe, zoom, video/model media
 *  StickyATCBar     — sticky add-to-cart bar that mirrors main form
 *  ProductAccordion — animated accordion with optional exclusive mode
 *
 * Global helpers:
 *  formatMoney(cents, moneyFormat)
 *  getSelectedVariant(product, selectedOptions)
 *
 * Event contract:
 *  Dispatches  → cart:updated            (document, bubbles) — cart-drawer.js opens on this
 *  Dispatches  → product:variant-changed (document, bubbles) — {variant}
 *  Dispatches  → product:media-change    (document, bubbles) — {mediaIndex}
 *  Listens to  → product:media-change    (document)          — ProductGallery
 */

// =============================================================================
// Global helpers
// =============================================================================

/**
 * Format a price in cents. Thin wrapper over the shared formatter in money.js;
 * the local fallback only matters if that asset fails to load.
 * @param {number} cents
 * @param {string} [moneyFormat]
 */
function formatMoney(cents, moneyFormat) {
  if (window.Skeleton?.money) return window.Skeleton.money(cents, moneyFormat);
  return cents == null ? '' : (cents / 100).toFixed(2);
}

/**
 * Find the variant that matches the given selected options array.
 * selectedOptions: ['Red', 'Large', ...]  — order matches product.options order.
 */
function getSelectedVariant(product, selectedOptions) {
  if (!product?.variants || !selectedOptions?.length) return null;
  return product.variants.find(v =>
    v.options.every((opt, i) => opt === selectedOptions[i])
  ) || null;
}

function isPreorderVariant(variant, product) {
  if (!variant?.available) return false;

  const rawTags = product?.tags || [];
  const tags = (Array.isArray(rawTags) ? rawTags : String(rawTags).split(',')).map(tag => String(tag).trim().toLowerCase());
  if (tags.some(tag => tag === 'preorder' || tag === 'pre-order')) return true;

  return (
    variant.inventory_management === 'shopify' &&
    variant.inventory_policy === 'continue' &&
    Number(variant.inventory_quantity || 0) <= 0
  );
}

function getVariantQuantityMax(variant) {
  if (!variant) return null;

  const ruleMax = Number(variant.quantity_rule?.max || 0) || null;
  const stockMax = (
    variant.inventory_management === 'shopify' &&
    variant.inventory_policy !== 'continue' &&
    Number(variant.inventory_quantity || 0) > 0
  )
    ? Number(variant.inventory_quantity)
    : null;

  if (ruleMax && stockMax) return Math.min(ruleMax, stockMax);
  return ruleMax || stockMax || null;
}

function clampQuantityInput(input, max = null) {
  if (!input) return { value: 1, wasClamped: false };

  const min = parseInt(input.min, 10) || 1;
  const inputMax = max || parseInt(input.max, 10) || Infinity;
  const rawValue = parseInt(input.value, 10) || min;
  const value = Math.min(inputMax, Math.max(min, rawValue));
  const wasClamped = value !== rawValue;

  if (wasClamped) {
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  return { value, wasClamped };
}

function scheduleQuantityClamp(input, max = null) {
  if (!input) return;

  window.clearTimeout(input._pdpQuantityClampTimer);
  input._pdpQuantityClampTimer = window.setTimeout(() => {
    clampQuantityInput(input, max);
  }, 700);
}

function normalizeAvailabilityMessage(message) {
  const text = String(message || '');
  const limitedMatch = text.match(/Only\s+(\d+)\s+items?\s+were\s+added/i);
  if (limitedMatch) {
    return (window.theme?.strings?.product?.errorMaxQuantity || 'Only [count] can be added due to availability.')
      .replace('[count]', limitedMatch[1]);
  }
  return text;
}

function definePdpCustomElement(name, elementClass) {
  if (!window.customElements || window.customElements.get(name)) return;
  window.customElements.define(name, elementClass);
}

async function parsePdpCartResponse(response) {
  const text = await response.text();
  let data = {};

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { description: text };
    }
  }

  if (!response.ok) throw data;
  return data;
}

/**
 * Add a bundle add-on to the cart. Called by ProductMain's delegated click
 * handler; kept as a free function so the section markup stays declarative.
 * @param {HTMLElement} button
 */
async function addPdpBundleAddon(button) {
  const variantId = button?.dataset?.pdpBundleAdd;
  if (!variantId) return;

  const block = button.closest('.pdp__bundle-addons');
  const errorEl = block?.querySelector('[data-pdp-bundle-error]');
  if (errorEl) {
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  button.disabled = true;
  button.classList.add('is-loading');
  const originalText = button.textContent;
  button.textContent = window.theme?.strings?.product?.adding || 'Adding…';

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });
    const data = await parsePdpCartResponse(res);

    button.textContent = window.theme?.strings?.product?.added || 'Added!';
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { item: data },
    }));

    window.setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
      button.classList.remove('is-loading');
    }, 1600);
  } catch (err) {
    button.textContent = window.theme?.strings?.product?.tryAgain || 'Try again';
    button.disabled = false;
    button.classList.remove('is-loading');
    if (errorEl) {
      errorEl.textContent = normalizeAvailabilityMessage(err?.description || err?.message || window.theme?.strings?.product?.errorAddAddon || 'Could not add this add-on. Please try again.');
      errorEl.hidden = false;
    }
  }
}

// =============================================================================
// ProductMain
// =============================================================================

class ProductMain extends HTMLElement {
  connectedCallback() {
    this._product        = null;
    this._currentVariant = null;
    this._moneyFormat    = window.theme?.moneyFormat || '${{amount}}';

    this._init();
  }

  _init() {
    if (this._initialized) return;

    // Parse product JSON embedded in the section
    const jsonEl = this.querySelector('script[type="application/json"][data-product-json]');
    if (jsonEl) {
      try { this._product = JSON.parse(jsonEl.textContent); } catch {}
    }

    if (!this._product) {
      // product.js is loaded (without defer) above this element in the section,
      // so connectedCallback can fire before the embedded product JSON child is
      // parsed. Without it, variant reactivity (availability, gallery jump, SKU)
      // never wires up. Retry once the document has finished parsing.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
      }
      return;
    }

    this._initialized = true;

    // Resolve initial variant from URL or first available
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    const selectedVariantEl = this.querySelector('script[type="application/json"][data-selected-variant]');
    let liquidSelectedVariant = null;
    if (selectedVariantEl) {
      try { liquidSelectedVariant = JSON.parse(selectedVariantEl.textContent); } catch {}
    }

    const firstAvailableVariant = this._product.variants.find(v => v.available) || this._product.variants[0];
    this._currentVariant = urlVariantId
      ? this._product.variants.find(v => String(v.id) === urlVariantId) || firstAvailableVariant
      : this._product.variants.find(v => String(v.id) === String(liquidSelectedVariant?.id)) || firstAvailableVariant;

    // Delegated events on this element — survive DOM swaps
    this.addEventListener('change',  this._onChange.bind(this));
    this.addEventListener('input',   this._onInput.bind(this));
    this.addEventListener('focusout', this._onFocusOut.bind(this));
    this.addEventListener('click',   this._onClick.bind(this));
    this.addEventListener('submit',  this._onSubmit.bind(this));

    // Initialise display from current variant without triggering history change
    this._applyVariant(this._currentVariant, false);
  }

  // ---------------------------------------------------------------------------
  // Event delegation
  // ---------------------------------------------------------------------------

  _onInput(e) {
    if (e.target.matches('[data-qty-input]')) {
      scheduleQuantityClamp(e.target, getVariantQuantityMax(this._currentVariant));
    }
  }

  _onFocusOut(e) {
    if (e.target.matches('[data-qty-input]')) {
      clampQuantityInput(e.target, getVariantQuantityMax(this._currentVariant));
    }
  }

  _onChange(e) {
    // Swatch radio, pill radio, or native select
    const el = e.target;
    if (el.matches('[data-qty-input]')) {
      clampQuantityInput(el, getVariantQuantityMax(this._currentVariant));
      return;
    }

    if (
      el.matches('[data-option-selector]') ||
      el.closest('[data-option-selector]')
    ) {
      this._syncOptionControlStateFromInputs();
      this._resolveVariantFromSelectors();
    }
  }

  _onClick(e) {
    const swatchLabel = e.target.closest('.product-swatch');
    if (swatchLabel && !swatchLabel.closest('sticky-atc-bar')) {
      const input = swatchLabel.querySelector('[data-option-selector]');
      if (input?.type === 'radio' && !input.checked) {
        e.preventDefault();
        input.checked = true;
        this._syncOptionControlStateFromInputs();
        this._resolveVariantFromSelectors();
      }
      return;
    }

    /* "Explore other options" rows are sibling PRODUCTS, not variants of this
       one, so there is no option to select — the row instead becomes what the
       form adds. Only the price, availability and the form's variant id move;
       the gallery and copy stay put, because the shopper has not navigated. */
    const sizeRow = e.target.closest('.pdp__card[data-ep-variant]');
    if (sizeRow) {
      e.preventDefault();
      this._selectSiblingProduct(sizeRow);
      return;
    }

    const bundleAddBtn = e.target.closest('[data-pdp-bundle-add]');
    if (bundleAddBtn) {
      e.preventDefault();
      this._addBundleAddon(bundleAddBtn);
      return;
    }

    // Quantity stepper
    const stepBtn = e.target.closest('[data-qty-step]');
    if (stepBtn) {
      e.preventDefault();
      const input = this.querySelector('[data-qty-input]');
      if (!input) return;
      const step = parseInt(stepBtn.dataset.qtyStep, 10) || (stepBtn.matches('[data-qty-minus]') ? -1 : 1);
      const cur  = parseInt(input.value, 10) || 1;
      const min  = parseInt(input.min, 10)   || 1;
      const max  = parseInt(input.max, 10)   || Infinity;
      input.value = Math.min(max, Math.max(min, cur + step));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Notify-me form dismiss / confirmation (handled in _onSubmit)
  }

  _onSubmit(e) {
    // Notify-me form
    const notifyForm = e.target.closest('[data-notify-form]');
    if (notifyForm) {
      e.preventDefault();
      this._handleNotifySubmit(notifyForm);
      return;
    }

    // ATC form
    const atcForm = e.target.closest('[data-atc-form]');
    if (atcForm) {
      e.preventDefault();
      this._addToCart(atcForm);
    }
  }

  // ---------------------------------------------------------------------------
  // Variant resolution
  // ---------------------------------------------------------------------------

  _resolveVariantFromSelectors() {
    const selectorRoot = this.querySelector('[data-atc-form]') || this;
    const selectors = Array.from(selectorRoot.querySelectorAll('[data-option-selector]'));
    if (!selectors.length) return;

    // Group by option index; pick the checked/selected value for each
    const optionValues = [];
    const optionCount = this._product.options.length;

    for (let i = 0; i < optionCount; i++) {
      // Try radio/swatch (data-option-index matches i)
      const radio = selectorRoot.querySelector(
        `[data-option-index="${i}"][data-option-selector]:checked`
      );
      if (radio) { optionValues.push(radio.value); continue; }

      // Try select
      const select = selectorRoot.querySelector(
        `select[data-option-index="${i}"][data-option-selector]`
      );
      if (select) { optionValues.push(select.value); continue; }

      optionValues.push('');
    }

    const variant = getSelectedVariant(this._product, optionValues);
    if (!variant) return;

    this._currentVariant = variant;
    this._applyVariant(variant, true);
  }

  // ---------------------------------------------------------------------------
  // Apply variant — update all DOM regions
  // ---------------------------------------------------------------------------

  _applyVariant(variant, updateHistory) {
    if (!variant) return;

    if (updateHistory) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    this._updatePrice(variant);
    this._updateAvailability(variant);
    this._updateSku(variant);
    this._updateVariantInputs(variant);
    this._syncOptionControls(variant);
    this._updateVolumePricing(variant);
    this._updateMediaForVariant(variant);
    this._enforceQuantityRules(variant);

    document.dispatchEvent(new CustomEvent('product:variant-changed', {
      bubbles: true,
      detail: { variant },
    }));
  }

  _updatePrice(variant) {
    const moneyFormat = this._moneyFormat;

    // Main price
    const priceEl = this.querySelector('[data-product-price]') || this.querySelector('[data-price]');
    if (priceEl) priceEl.textContent = formatMoney(variant.price, moneyFormat);

    const stickyPriceEl = this.querySelector('[data-sticky-price]');
    if (stickyPriceEl) stickyPriceEl.textContent = formatMoney(variant.price, moneyFormat);

    const stickyVariantEl = this.querySelector('[data-sticky-variant-title]');
    if (stickyVariantEl && variant.title) stickyVariantEl.textContent = variant.title;

    // Loyalty points follow the price, so they follow the variant.
    // Whole units of spend only — £2.99 at 10 points per £1 earns 20, not 29.
    const pointsEl = this.querySelector('[data-points-block]');
    if (pointsEl) {
      const { rate: baseRate = 0, spendCents = 0 } = window.theme?.loyalty || {};
      // A campaign (2x points on a collection for a day) lifts the rate for
      // some products only, so the element carries its own when it differs.
      const rate = Number(pointsEl.dataset.pointsRate) || baseRate;
      const total = spendCents > 0 ? Math.floor(variant.price / spendCents) * rate : 0;
      pointsEl.querySelector('[data-points-value]').textContent = String(total);
      pointsEl.hidden = total <= 0;
    }

    // Compare-at price
    const compareEl = this.querySelector('[data-compare-price]');
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    // Sale badge
    const saleBadge = this.querySelector('[data-sale-badge]');
    if (saleBadge) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      saleBadge.hidden = !onSale;
      if (onSale) {
        // Rounded up (14.2 → 15), matching sale_pct in main-product.liquid.
        const pct = Math.ceil(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
        const pctEl = saleBadge.querySelector('[data-sale-percent]');
        // Template already renders a literal "%" after this span, so set the
        // number only — otherwise the badge reads "-25%%".
        if (pctEl) pctEl.textContent = String(pct);
      }
    }

    // Unit price
    const unitEl = this.querySelector('[data-unit-price]');
    if (unitEl) {
      if (variant.unit_price !== undefined && variant.unit_price !== null) {
        const measurement = variant.unit_price_measurement;
        const unitRef = measurement
          ? `${measurement.reference_value > 1 ? measurement.reference_value : ''}${measurement.reference_unit}`
          : '';
        unitEl.textContent = formatMoney(variant.unit_price, moneyFormat);
        const unitRefEl = this.querySelector('[data-unit-reference]');
        if (unitRefEl) unitRefEl.textContent = unitRef;
        const unitWrap = this.querySelector('[data-unit-price-wrapper]');
        if (unitWrap) unitWrap.hidden = false;
      } else {
        const unitWrap = this.querySelector('[data-unit-price-wrapper]');
        if (unitWrap) unitWrap.hidden = true;
      }
    }
  }

  _updateAvailability(variant) {
    const atcBtn       = this.querySelector('[data-atc-btn]');
    const stickyAtcBtn = this.querySelector('[data-sticky-atc-btn]');
    const stickyBuyNowBtn = this.querySelector('[data-sticky-buy-now-btn]');
    const notifyWrap   = this.querySelector('[data-notify-wrap]');
    const atcWrap      = this.querySelector('[data-atc-wrap]');
    const atcLabel     = atcBtn?.querySelector('[data-atc-label]') || atcBtn?.querySelector('[data-atc-btn-text]') || atcBtn;

    const available    = variant.available;
    const soldOut      = !available;
    const preorder     = isPreorderVariant(variant, this._product);
    const buttonText   = soldOut
      ? (atcBtn?.dataset.soldOutText || 'Sold out')
      : preorder
        ? (atcBtn?.dataset.preorderText || 'Pre-order')
        : (atcBtn?.dataset.addText || 'Add to cart');

    if (atcBtn) {
      atcBtn.disabled = soldOut;
      atcBtn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
    }

    if (atcLabel) atcLabel.textContent = buttonText;

    if (stickyAtcBtn) {
      stickyAtcBtn.disabled = soldOut;
      stickyAtcBtn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
      const stickyLabel = stickyAtcBtn.querySelector('[data-sticky-atc-label]');
      const stickyText = soldOut
        ? (stickyAtcBtn.dataset.soldOutText || 'Sold out')
        : preorder
          ? (stickyAtcBtn.dataset.preorderText || 'Pre-order')
          : (stickyAtcBtn.dataset.addText || 'Add to cart');
      if (stickyLabel) stickyLabel.textContent = stickyText;
      else stickyAtcBtn.textContent = stickyText;
    }

    if (stickyBuyNowBtn) {
      stickyBuyNowBtn.disabled = soldOut;
      stickyBuyNowBtn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
    }

    // Show/hide notify-me vs ATC. The notify wrapper is rendered with a
    // `hidden` CSS class based on the INITIAL variant, so toggle the class too —
    // clearing the `hidden` attribute alone leaves `display:none` in place.
    if (notifyWrap) {
      notifyWrap.hidden = available;
      notifyWrap.classList.toggle('hidden', available);
    }
    if (atcWrap) {
      atcWrap.hidden = !available;
      atcWrap.classList.toggle('hidden', !available);
    }

    // Availability text badge
    const availEl = this.querySelector('[data-availability]');
    if (availEl) {
      availEl.textContent = available ? (availEl.dataset.inStockText || 'In stock') : (availEl.dataset.soldOutText || 'Sold out');
      availEl.dataset.available = available ? 'true' : 'false';
    }
  }

  _updateSku(variant) {
    const skuEl = this.querySelector('[data-product-sku]') || this.querySelector('[data-variant-sku]');
    if (!skuEl) return;
    if (variant.sku) {
      skuEl.textContent = variant.sku;
      skuEl.closest('[data-sku-wrap]')?.removeAttribute('hidden');
    } else {
      const wrap = skuEl.closest('[data-sku-wrap]');
      if (wrap) wrap.hidden = true;
    }
  }

  /* A sibling product's variant, built from the row's data- attributes. It is
     deliberately NOT run through _applyVariant: that syncs option controls and
     media against this product's own variants, which a foreign id never
     matches. _addToCart reads _currentVariant, so setting it is what makes the
     button add the chosen size. */
  _selectSiblingProduct(row) {
    this.querySelectorAll('.pdp__card[data-ep-variant]').forEach((el) => {
      el.classList.toggle('is-selected', el === row);
      if (el === row) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });

    const d = row.dataset;
    const variant = {
      id: Number(d.epVariant),
      price: Number(d.epPrice) || 0,
      compare_at_price: Number(d.epCompare) || null,
      available: d.epAvailable === 'true',
      title: d.epTitle || '',
    };

    this._currentVariant = variant;
    this._updatePrice(variant);
    this._updateAvailability(variant);
    this._updateVariantInputs(variant);
  }

  _updateVariantInputs(variant) {
    this.querySelectorAll('[data-variant-id]').forEach(el => {
      el.value = variant.id;
    });
  }

  _syncOptionControls(variant) {
    if (!variant?.options) return;

    variant.options.forEach((value, index) => {
      this.querySelectorAll(`[data-option-index="${index}"][data-option-selector]`).forEach(control => {
        if (control.tagName === 'SELECT') {
          control.value = value;
        } else {
          control.checked = control.value === value;
        }
      });

      const selectedLabel = this.querySelector(`[data-option-selected="${index + 1}"]`);
      if (selectedLabel) selectedLabel.textContent = value;
    });

    this._syncOptionControlStateFromInputs();
  }

  _syncOptionControlStateFromInputs() {
    const selectorRoot = this.querySelector('[data-atc-form]') || this;

    this.querySelectorAll('.pdp__pill, .product-swatch').forEach(label => {
      const input = label.querySelector('[data-option-selector]');
      if (!input) return;
      const isSelected = input.checked;
      label.classList.toggle('is-selected', isSelected);
      label.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    for (let i = 0; i < this._product.options.length; i++) {
      const selectedLabel = this.querySelector(`[data-option-selected="${i + 1}"]`);
      if (!selectedLabel) continue;

      const checked = selectorRoot.querySelector(
        `[data-option-index="${i}"][data-option-selector]:checked`
      );
      const select = selectorRoot.querySelector(
        `select[data-option-index="${i}"][data-option-selector]`
      );
      selectedLabel.textContent = checked?.value || select?.value || selectedLabel.textContent;
    }
  }

  _updateVolumePricing(variant) {
    const container = this.querySelector('[data-volume-pricing]');
    if (!container || !variant) return;

    let hasActivePanel = false;
    container.querySelectorAll('[data-volume-variant-id]').forEach(panel => {
      const isActive = String(panel.dataset.volumeVariantId) === String(variant.id);
      const hasRows = panel.querySelector('.pdp__vol-row') !== null;
      panel.hidden = !(isActive && hasRows);
      if (isActive && hasRows) hasActivePanel = true;
    });

    container.hidden = !hasActivePanel;
  }

  _updateMediaForVariant(variant) {
    if (!this._product?.media?.length) return;

    let idx = -1;
    if (variant.featured_media?.id) {
      const mediaId = variant.featured_media.id;
      idx = this._product.media.findIndex(m => m.id === mediaId);
    }

    if (idx < 0 && variant.options?.length) {
      const controls = Array.from(this.querySelectorAll('[data-option-selector][data-option-index="0"]'));
      const values = [...new Set(controls.map(control => control.value).filter(Boolean))];
      const valueIndex = values.indexOf(variant.options[0]);
      if (valueIndex >= 0 && valueIndex < this._product.media.length) idx = valueIndex;
    }

    if (idx < 0) return;

    document.dispatchEvent(new CustomEvent('product:media-change', {
      bubbles: true,
      detail: { mediaIndex: idx },
    }));
  }

  _enforceQuantityRules(variant) {
    const qtyInput = this.querySelector('[data-qty-input]');
    if (!qtyInput) return;
    const rules = variant.quantity_rule || {};

    const min = rules.min || 1;
    const max = getVariantQuantityMax(variant);
    const inc = rules.increment || 1;

    qtyInput.min  = min;
    qtyInput.step = inc;
    if (max) qtyInput.max = max;
    else qtyInput.removeAttribute('max');

    clampQuantityInput(qtyInput, max);
  }

  // ---------------------------------------------------------------------------
  // AJAX Add to Cart
  // ---------------------------------------------------------------------------

  async _addToCart(form) {
    const atcBtn   = form.querySelector('[data-atc-btn]') || this.querySelector('[data-atc-btn]');
    const errorEl  = this.querySelector('[data-atc-error]');

    if (!this._currentVariant) return;

    if (errorEl) errorEl.hidden = true;

    const variantId = this._currentVariant.id;
    const qtyInput  = form.querySelector('[data-qty-input]') || this.querySelector('[data-qty-input]');
    const maxQty = getVariantQuantityMax(this._currentVariant);
    const quantityState = clampQuantityInput(qtyInput, maxQty);
    const qty = quantityState.value;

    if (quantityState.wasClamped && maxQty) {
      this._showAtcError(errorEl, (window.theme?.strings?.product?.errorMaxQuantity || 'Only [count] can be added due to availability.').replace('[count]', maxQty));
    }

    // Collect selling plan if present
    const sellingPlanInput = form.querySelector('[name="selling_plan"]');
    const sellingPlan = sellingPlanInput ? sellingPlanInput.value : null;

    // Collect properties
    const properties = {};
    form.querySelectorAll('[name^="properties["]').forEach(input => {
      const key = input.name.match(/properties\[(.+)\]/)?.[1];
      if (key) properties[key] = input.value;
    });

    // Show loading state
    const stickyBtn = document.querySelector('[data-sticky-atc-btn]');
    [atcBtn, stickyBtn].forEach((btn) => {
      if (!btn) return;
      btn.disabled = true;
      btn.classList.add('is-loading');
    });

    // Open the cart drawer immediately so the user gets instant visual feedback
    // while the network request is in flight.
    document.dispatchEvent(new CustomEvent('cart:opening', { bubbles: true }));

    const body = { id: variantId, quantity: qty };
    if (sellingPlan) body.selling_plan = sellingPlan;
    if (Object.keys(properties).length) body.properties = properties;

    try {
      const res  = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(body),
      });

      const data = await parsePdpCartResponse(res);

      // Success — dispatch to cart-drawer
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { item: data },
      }));

      // Brief success state on button
      this._showAtcSuccess(atcBtn);

    } catch (err) {
      this._showAtcError(errorEl, normalizeAvailabilityMessage(err?.description || err?.message || window.theme?.strings?.product?.errorAdd || 'Unable to add to cart. Please try again.'));
    } finally {
      if (atcBtn) atcBtn.classList.remove('is-loading');
      document.querySelector('[data-sticky-atc-btn]')?.classList.remove('is-loading');
    }
  }

  async _addBundleAddon(button) {
    return addPdpBundleAddon(button);
  }

  _showAtcError(errorEl, msg) {
    const atcBtn = this.querySelector('[data-atc-btn]');
    const stickyBtn = document.querySelector('[data-sticky-atc-btn]');
    [atcBtn, stickyBtn].forEach((btn) => {
      if (!btn) return;
      btn.disabled = false;
      btn.classList.remove('is-loading');
    });
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
    clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => { if (errorEl) errorEl.hidden = true; }, 5000);
  }

  _showAtcSuccess(atcBtn) {
    if (!atcBtn) return;
    const label   = atcBtn.querySelector('[data-atc-label]');
    const origText = label ? label.textContent : null;
    const successText = atcBtn.dataset.successText || 'Added!';

    if (label) label.textContent = successText;
    atcBtn.classList.add('is-success');
    const stickyBtn = document.querySelector('[data-sticky-atc-btn]');
    stickyBtn?.classList.add('is-success');

    setTimeout(() => {
      atcBtn.disabled = false;
      atcBtn.classList.remove('is-success');
      if (label && origText) label.textContent = origText;
      if (stickyBtn) {
        stickyBtn.disabled = false;
        stickyBtn.classList.remove('is-success');
      }
    }, 1800);
  }

  // ---------------------------------------------------------------------------
  // Notify me
  // ---------------------------------------------------------------------------

  _handleNotifySubmit(form) {
    const emailInput  = form.querySelector('[data-notify-email]') || form.querySelector('input[type="email"]');
    const confirmation = this.querySelector('[data-notify-confirmation]');

    // Basic validation
    if (emailInput && !emailInput.value.includes('@')) {
      emailInput.setCustomValidity(window.theme?.strings?.product?.errorEmailInvalid || 'Please enter a valid email.');
      emailInput.reportValidity();
      return;
    }
    emailInput?.setCustomValidity('');

    // Show confirmation — actual submission would go to an app endpoint
    form.hidden = true;
    if (confirmation) confirmation.hidden = false;
  }
}

// =============================================================================
// ProductGallery
// =============================================================================

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this._activeIndex  = 0;
    this._mediaCount   = 0;
    this._touchStartX  = 0;
    this._touchStartY  = 0;
    this._isZoomed     = false;
    this._galleryStyle = this.dataset.galleryStyle || 'carousel';

    this._init();
  }

  _init() {
    if (this._initialized) return;

    // Media items — images, video, model
    this._mediaItems = Array.from(this.querySelectorAll('[data-media-item]'));
    this._mediaCount = this._mediaItems.length;

    // Thumbnails
    this._thumbItems = Array.from(this.querySelectorAll('[data-thumb-item]'));

    // Main image / media viewer
    this._mainViewer = this.querySelector('[data-main-viewer]');
    this._thumbWrap = this.querySelector('[data-gallery-thumbs-wrap]');
    this._thumbRail = this.querySelector('[data-gallery-thumbs]');
    this._thumbPrev = this.querySelector('[data-thumbs-prev]');
    this._thumbNext = this.querySelector('[data-thumbs-next]');

    // Counter
    this._counter = this.querySelector('[data-gallery-counter]');
    this._arrowPrev = this.querySelector('[data-gallery-prev]');
    this._arrowNext = this.querySelector('[data-gallery-next]');
    this._dotItems = Array.from(this.querySelectorAll('[data-dot-index]'));

    if (!this._mediaItems.length) {
      // product.js is loaded (without defer) above this element, so
      // connectedCallback can run before the slide children are parsed. Retry
      // once the document is parsed so the gallery wires up its media items.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
      }
      return;
    }

    this._initialized = true;

    // Thumbnail clicks
    this._thumbItems.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setActive(i));
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.setActive(i); }
      });
    });

    this._boundThumbScrollState = this._updateThumbScrollIndicators.bind(this);
    this._thumbRail?.addEventListener('scroll', this._boundThumbScrollState, { passive: true });
    this._thumbPrev?.addEventListener('click', () => this._scrollThumbs(-1));
    this._thumbNext?.addEventListener('click', () => this._scrollThumbs(1));

    this._dotItems.forEach((dot, i) => {
      dot.addEventListener('click', () => this.setActive(i));
    });

    // Prev/next arrows over the main viewer (thumbnail position "Dots with
    // arrows"). Same clamped step the swipe handler uses.
    this._arrowPrev?.addEventListener('click', () => {
      this.setActive(Math.max(this._activeIndex - 1, 0));
    });
    this._arrowNext?.addEventListener('click', () => {
      this.setActive(Math.min(this._activeIndex + 1, this._mediaCount - 1));
    });

    // Editorial layout: reveal the tiles held back behind "View all images".
    // One-shot — the button removes itself once everything is on screen.
    this._editorialMore = this.querySelector('[data-editorial-more]');
    this._editorialMore?.addEventListener('click', () => {
      this.querySelectorAll('[data-editorial-extra]').forEach((tile) => {
        tile.hidden = false;
        tile.removeAttribute('data-editorial-extra');
      });
      this._editorialMore.remove();
      this._editorialMore = null;
    });

    // Desktop zoom on main image
    if (this._mainViewer) {
      this._mainViewer.addEventListener('click',       this._onMainClick.bind(this));
      this._mainViewer.addEventListener('mouseenter',  this._onMainMouseEnter.bind(this));
      this._mainViewer.addEventListener('mouseleave',  this._onMainMouseLeave.bind(this));
    }

    // Mobile swipe
    this.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: true });
    this.addEventListener('touchend',   this._onTouchEnd.bind(this),   { passive: true });

    // Listen for variant-driven media changes
    this._boundMediaChange = this._onMediaChange.bind(this);
    document.addEventListener('product:media-change', this._boundMediaChange);
    this._boundLightboxKeydown = this._onLightboxKeydown.bind(this);

    // Lightbox overlay (created once, reused)
    this._lightbox = null;

    // Init first slide
    this.setActive(0, false);
    this._syncThumbRailSize();
    this._boundThumbRailSize = this._syncThumbRailSize.bind(this);
    window.addEventListener('resize', this._boundThumbRailSize);
    this._resizeObserver = window.ResizeObserver && this._mainViewer
      ? new ResizeObserver(this._boundThumbRailSize)
      : null;
    this._resizeObserver?.observe(this._mainViewer);
    this._updateThumbScrollIndicators();
    this._scheduleImagePreload();
  }

  disconnectedCallback() {
    document.removeEventListener('product:media-change', this._boundMediaChange);
    document.removeEventListener('keydown', this._boundLightboxKeydown);
    this._thumbRail?.removeEventListener('scroll', this._boundThumbScrollState);
    window.removeEventListener('resize', this._boundThumbRailSize);
    this._resizeObserver?.disconnect();
  }

  _syncThumbRailSize() {
    if (!this._mainViewer || !this._thumbRail) return;
    const height = Math.round(this._mainViewer.getBoundingClientRect().height);
    if (height > 0) this.style.setProperty('--pdp-gallery-height', `${height}px`);
    this._updateThumbScrollIndicators();
  }

  _isThumbRailVertical() {
    if (!this._thumbRail) return false;
    if (this._thumbWrap?.classList.contains('pdp__thumb-slider--vertical')) return true;
    if (this._thumbWrap?.classList.contains('pdp__thumb-slider--horizontal')) return false;
    const verticalOverflow = this._thumbRail.scrollHeight - this._thumbRail.clientHeight;
    const horizontalOverflow = this._thumbRail.scrollWidth - this._thumbRail.clientWidth;
    return verticalOverflow > horizontalOverflow;
  }

  _scrollThumbs(direction) {
    if (!this._thumbRail) return;
    const vertical = this._isThumbRailVertical();
    const max = vertical
      ? this._thumbRail.scrollHeight - this._thumbRail.clientHeight
      : this._thumbRail.scrollWidth - this._thumbRail.clientWidth;
    const current = vertical ? this._thumbRail.scrollTop : this._thumbRail.scrollLeft;
    const amount = vertical
      ? this._thumbRail.clientHeight * 0.75
      : this._thumbRail.clientWidth * 0.75;
    const nextPosition = Math.max(0, Math.min(max, current + (direction * amount)));

    this._thumbRail.scrollTo({
      top: vertical ? nextPosition : 0,
      left: vertical ? 0 : nextPosition,
      behavior: 'smooth',
    });
    window.setTimeout(() => this._updateThumbScrollIndicators(), 220);
  }

  _updateThumbScrollIndicators() {
    if (!this._thumbRail || !this._thumbWrap) return;

    const vertical = this._isThumbRailVertical();
    const position = vertical ? this._thumbRail.scrollTop : this._thumbRail.scrollLeft;
    const max = vertical
      ? this._thumbRail.scrollHeight - this._thumbRail.clientHeight
      : this._thumbRail.scrollWidth - this._thumbRail.clientWidth;
    const canScroll = max > 2;
    const canScrollStart = canScroll && position > 2;
    const canScrollEnd = canScroll && position < max - 2;

    this._thumbWrap.classList.toggle('can-scroll-start', canScrollStart);
    this._thumbWrap.classList.toggle('can-scroll-end', canScrollEnd);
    if (this._thumbPrev) this._thumbPrev.disabled = !canScrollStart;
    if (this._thumbNext) this._thumbNext.disabled = !canScrollEnd;
  }

  _scheduleImagePreload() {
    const run = () => this._preloadGalleryImages();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1200 });
    } else {
      window.setTimeout(run, 300);
    }
  }

  _preloadGalleryImages() {
    this._mediaItems.forEach((item, index) => {
      const image = item.querySelector('.pdp__gallery-img');
      if (!image || image.dataset.preloaded === 'true') return;

      image.dataset.preloaded = 'true';
      if (index > 0) {
        image.loading = 'eager';
        image.fetchPriority = 'low';
      }

      const preloader = new Image();
      if (image.sizes) preloader.sizes = image.sizes;
      if (image.srcset) preloader.srcset = image.srcset;
      preloader.src = image.currentSrc || image.src;
      preloader.decode?.().catch(() => {});
    });
  }

  // ---------------------------------------------------------------------------
  // Set active media
  // ---------------------------------------------------------------------------

  setActive(index, animate = true) {
    if (index < 0 || index >= this._mediaCount) return;
    const prev = this._activeIndex;
    this._activeIndex = index;
    const isCarousel = this._galleryStyle === 'carousel';

    // Update media items
    this._mediaItems.forEach((item, i) => {
      const isActive = i === index;
      const isPrevious = i === prev;
      item.classList.remove(
        'is-entering',
        'is-leaving',
        'is-entering-from-next',
        'is-entering-from-prev',
        'is-leaving-to-next',
        'is-leaving-to-prev'
      );
      if (isCarousel) {
        item.hidden = !(isActive || (animate && isPrevious));
      }
      item.classList.toggle('is-active', isActive && !(isCarousel && animate && prev !== index));
      item.setAttribute('aria-hidden', isCarousel ? (isActive ? 'false' : 'true') : 'false');
    });

    if (isCarousel && animate && prev !== index) {
      const activeItem = this._mediaItems[index];
      const prevItem = this._mediaItems[prev];
      const direction = index > prev ? 'next' : 'prev';

      activeItem?.classList.add(
        'is-entering',
        direction === 'next' ? 'is-entering-from-next' : 'is-entering-from-prev'
      );
      prevItem?.classList.add('is-active');
      activeItem?.getBoundingClientRect();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          activeItem?.classList.remove('is-entering-from-next', 'is-entering-from-prev');
          activeItem?.classList.add('is-active');
          prevItem?.classList.add(
            'is-leaving',
            direction === 'next' ? 'is-leaving-to-next' : 'is-leaving-to-prev'
          );
          prevItem?.classList.remove('is-active');
        });
      });

      window.setTimeout(() => {
        activeItem?.classList.remove('is-entering');
        prevItem?.classList.remove('is-leaving', 'is-leaving-to-next', 'is-leaving-to-prev');
        if (prevItem && !prevItem.classList.contains('is-active')) prevItem.hidden = true;
      }, 450);
    }

    // Update thumbnails
    this._thumbItems.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });

    this._dotItems.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    // Nothing before the first slide or after the last, so that arrow goes
    // disabled rather than being a dead click.
    if (this._arrowPrev) this._arrowPrev.disabled = index === 0;
    if (this._arrowNext) this._arrowNext.disabled = index === this._mediaCount - 1;

    window.setTimeout(() => this._updateThumbScrollIndicators(), 120);

    // Update counter text "2 / 5"
    if (this._counter) {
      this._counter.textContent = `${index + 1} / ${this._mediaCount}`;
    }

    // Pause any playing videos in the previous slide
    const prevItem = this._mediaItems[prev];
    if (prevItem && prev !== index) {
      const video = prevItem.querySelector('video');
      video?.pause();
      const iframe = prevItem.querySelector('iframe[data-video-src]');
      if (iframe) {
        // Stop YouTube/Vimeo by reloading src
        const src = iframe.dataset.videoSrc;
        if (src) iframe.src = src;
      }
    }

    // Lazy-load the newly active media
    const activeItem = this._mediaItems[index];
    if (activeItem) {
      const lazyImg = activeItem.querySelector('img[data-src]');
      if (lazyImg && !lazyImg.src) {
        lazyImg.src     = lazyImg.dataset.src;
        lazyImg.srcset  = lazyImg.dataset.srcset || '';
      }
      const lazyIframe = activeItem.querySelector('iframe[data-src]');
      if (lazyIframe && !lazyIframe.src) {
        lazyIframe.src = lazyIframe.dataset.src;
      }
    }

    // Reset zoom when switching slides
    if (this._isZoomed) this._resetZoom();
  }

  // ---------------------------------------------------------------------------
  // Zoom
  // ---------------------------------------------------------------------------

  _onMainMouseEnter() {
    if (!this._isZoomed) this._mainViewer.style.cursor = 'zoom-in';
  }

  _onMainMouseLeave() {
    if (!this._isZoomed) this._mainViewer.style.cursor = '';
  }

  _onMainClick(e) {
    if (!this.hasAttribute('data-enable-image-lightbox')) return;

    // Don't zoom on video or model items
    const active = this._mediaItems[this._activeIndex];
    if (!active) return;
    if (active.dataset.mediaType === 'video' || active.dataset.mediaType === 'model') return;

    if (this._isZoomed) {
      this._resetZoom();
    } else {
      this._openLightbox(active);
    }
  }

  _openLightbox(mediaItem) {
    this._isZoomed = true;
    this._mainViewer.style.cursor = 'zoom-out';

    if (!this._lightbox) {
      const lb = document.createElement('div');
      lb.className = 'product-gallery__lightbox';
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', window.theme?.strings?.product?.imageZoom || 'Image zoom');
      lb.setAttribute('tabindex', '-1');
      lb.hidden = true;

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'product-gallery__lightbox-close';
      close.setAttribute('aria-label', window.theme?.strings?.product?.closeZoom || 'Close full-screen image');
      close.textContent = '×';

      const img = document.createElement('img');
      img.className = 'product-gallery__lightbox-img';
      img.addEventListener('click', (event) => event.stopPropagation());

      lb.appendChild(close);
      lb.appendChild(img);

      lb.addEventListener('click', () => this._resetZoom());
      close.addEventListener('click', (event) => {
        event.stopPropagation();
        this._resetZoom();
      });

      document.body.appendChild(lb);
      this._lightbox = lb;
    }

    this._syncLightboxImage();
    this._lightbox.hidden = false;
    document.addEventListener('keydown', this._boundLightboxKeydown);
    document.body.style.overflow = 'hidden';
    this._lightbox.focus({ preventScroll: true });
  }

  _syncLightboxImage() {
    if (!this._lightbox) return;
    const active = this._mediaItems[this._activeIndex];
    if (!active) return;
    const srcImg = active.querySelector('img');
    const lbImg  = this._lightbox.querySelector('img');
    if (!lbImg || !srcImg) return;
    const fullSrc = srcImg.dataset.zoomSrc || srcImg.src || srcImg.dataset.src || '';
    lbImg.src = fullSrc;
    lbImg.alt = srcImg.alt || '';
  }

  _onLightboxKeydown(e) {
    if (!this._isZoomed) return;
    if (e.key === 'Escape') this._resetZoom();
    if (e.key === 'ArrowRight') {
      this.setActive(Math.min(this._activeIndex + 1, this._mediaCount - 1));
      this._syncLightboxImage();
    }
    if (e.key === 'ArrowLeft') {
      this.setActive(Math.max(this._activeIndex - 1, 0));
      this._syncLightboxImage();
    }
  }

  _resetZoom() {
    this._isZoomed = false;
    if (this._mainViewer) this._mainViewer.style.cursor = 'zoom-in';
    if (this._lightbox) this._lightbox.hidden = true;
    document.removeEventListener('keydown', this._boundLightboxKeydown);
    document.body.style.overflow = '';
    this._mainViewer.style.cursor = '';
  }

  // ---------------------------------------------------------------------------
  // Mobile swipe
  // ---------------------------------------------------------------------------

  _onTouchStart(e) {
    const touch = e.touches[0];
    this._touchStartX = touch.clientX;
    this._touchStartY = touch.clientY;
  }

  _onTouchEnd(e) {
    const touch = e.changedTouches[0];
    const dx = touch.clientX - this._touchStartX;
    const dy = touch.clientY - this._touchStartY;

    // Require predominantly horizontal swipe, min 40px
    if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;

    if (dx < 0) {
      this.setActive(Math.min(this._activeIndex + 1, this._mediaCount - 1));
    } else {
      this.setActive(Math.max(this._activeIndex - 1, 0));
    }
  }

  // ---------------------------------------------------------------------------
  // Variant-driven media change
  // ---------------------------------------------------------------------------

  _onMediaChange(e) {
    const idx = e.detail?.mediaIndex;
    if (typeof idx === 'number') this.setActive(idx);
  }
}

// =============================================================================
// StickyATCBar
// =============================================================================

class StickyATCBar extends HTMLElement {
  connectedCallback() {
    this._mainForm    = null;
    this._productMain = null;
    const buyNowBtn = this.querySelector('[data-sticky-buy-now-btn]');
    if (buyNowBtn) buyNowBtn.textContent = window.theme?.strings?.product?.buyItNow || 'Buy it now';

    // Find the main ATC button to observe
    const observeSelector = this.dataset.observeSelector || '[data-atc-btn-anchor]';
    this._anchorEl = document.querySelector(observeSelector)
      || document.querySelector('[data-atc-btn]');
    this._footerEl = null;
    this._boundUpdateVisibility = this._updateVisibility.bind(this);

    if (this._anchorEl) {
      this._observer = new IntersectionObserver(
        this._boundUpdateVisibility,
        { threshold: 0 }
      );
      this._observer.observe(this._anchorEl);
    }
    window.addEventListener('scroll', this._boundUpdateVisibility, { passive: true });
    window.addEventListener('resize', this._boundUpdateVisibility);
    this._boundUpdateVisibility();

    // Delegated click/change on the sticky bar
    this.addEventListener('click',  this._onClick.bind(this));
    this.addEventListener('change', this._onChange.bind(this));

    // Shopify's payment-button script appends an empty <div style="clear:both">
    // into this bar. It is not in the theme, so drop it whenever it appears.
    const stripClear = () => {
      this.querySelectorAll('div').forEach((el) => {
        if (el.style.clear === 'both' && el.childElementCount === 0) el.remove();
      });
    };
    stripClear();
    this._clearObserver = new MutationObserver(stripClear);
    this._clearObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._clearObserver?.disconnect();
    window.removeEventListener('scroll', this._boundUpdateVisibility);
    window.removeEventListener('resize', this._boundUpdateVisibility);
  }

  _updateVisibility() {
    // Show sticky bar only after the main ATC has scrolled past the viewport.
    // If the main ATC is still below the fold, keep the sticky bar hidden.
    const rect = this._anchorEl?.getBoundingClientRect();
    this._footerEl = this._footerEl
      || document.querySelector('footer')
      || document.querySelector('[id*="footer"]');
    const footerRect = this._footerEl?.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const stickyHeight = this.offsetHeight || 0;
    // Hide the bar only once the footer actually reaches the sticky bar's top
    // edge (which sits at viewportHeight - stickyHeight). The previous
    // `viewportHeight + stickyHeight` threshold flagged the footer as in-view a
    // full bar-height *below* the fold, which on shorter pages closed the
    // visibility window entirely and the bar never appeared.
    const footerInView = Boolean(footerRect && footerRect.top < viewportHeight - stickyHeight);
    const shouldShow = Boolean(rect && rect.bottom < 0 && !footerInView);
    this.classList.toggle('is-visible', shouldShow);
    this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  _onClick(e) {
    const swatchLabel = e.target.closest('.pdp__sticky-swatch');
    if (swatchLabel) {
      const input = swatchLabel.querySelector('[data-option-selector]');
      if (input?.type === 'radio' && !input.checked) {
        e.preventDefault();
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const buyNowBtn = e.target.closest('[data-sticky-buy-now-btn]');
    if (buyNowBtn) {
      e.preventDefault();
      const dynamicCheckoutBtn = document.querySelector(
        '[data-dynamic-checkout] .shopify-payment-button__button'
      );
      if (dynamicCheckoutBtn && !dynamicCheckoutBtn.disabled) {
        dynamicCheckoutBtn.click();
      }
      return;
    }

    const atcBtn = e.target.closest('[data-sticky-atc-btn]');
    if (!atcBtn) return;
    e.preventDefault();

    // Delegate to main form submit
    const mainForm = document.querySelector('[data-atc-form]');
    if (mainForm) {
      mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  _onChange(e) {
    const stickySelector = e.target.closest('[data-option-selector]');
    if (!stickySelector) return;

    this._syncSelectedControls();

    // Mirror the change to the main form selector
    const optionIndex = stickySelector.dataset.optionIndex;
    const value       = stickySelector.value;

    const mainSelector = document.querySelector(
      `[data-atc-form] [data-option-selector][data-option-index="${optionIndex}"]`
    ) || document.querySelector(
      `product-main [data-option-selector][data-option-index="${optionIndex}"]`
    );

    if (!mainSelector) return;

    document.addEventListener('product:variant-changed', (ev) => {
      this._syncPriceDisplay(ev.detail.variant);
    }, { once: true });

    if (mainSelector.type === 'radio' || mainSelector.type === 'checkbox') {
      // Find the matching radio
      const matching = document.querySelector(
        `product-main [data-option-selector][data-option-index="${optionIndex}"][value="${CSS.escape(value)}"]`
      );
      if (matching) {
        matching.checked = true;
        matching.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      mainSelector.value = value;
      mainSelector.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _syncPriceDisplay(variant) {
    if (!variant) return;
    const moneyFormat = window.theme?.moneyFormat || '${{amount}}';

    const priceEl = this.querySelector('[data-sticky-price]');
    if (priceEl) priceEl.textContent = formatMoney(variant.price, moneyFormat);

    const variantEl = this.querySelector('[data-sticky-variant-title]');
    if (variantEl && variant.title) variantEl.textContent = variant.title;

    this._syncSelectedControls(variant);
  }

  _syncSelectedControls(variant) {
    this.querySelectorAll('[data-option-selector]').forEach(control => {
      if (variant?.options?.[Number(control.dataset.optionIndex)] != null) {
        const value = variant.options[Number(control.dataset.optionIndex)];
        if (control.tagName === 'SELECT') {
          control.value = value;
        } else {
          control.checked = control.value === value;
        }
      }
    });

    this.querySelectorAll('.product-swatch').forEach(label => {
      const input = label.querySelector('[data-option-selector]');
      if (!input) return;
      const isSelected = input.checked;
      label.classList.toggle('is-selected', isSelected);
      label.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
  }
}

// =============================================================================
// ProductAccordion
// =============================================================================

class ProductAccordion extends HTMLElement {
  connectedCallback() {
    this._exclusive = this.hasAttribute('data-exclusive');

    // Each accordion item needs [data-accordion-item] wrapping
    // [data-accordion-trigger] (button) and [data-accordion-body]
    this.addEventListener('click', this._onClick.bind(this));

    // Ensure initial ARIA state is set
    this.querySelectorAll('[data-accordion-trigger]').forEach(trigger => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const bodyId   = trigger.getAttribute('aria-controls');
      const body     = bodyId ? document.getElementById(bodyId) : trigger.nextElementSibling;
      if (body) {
        // The panel ships with the `hidden` attribute (display:none) for the
        // no-JS/collapsed case. Once JS controls the accordion, visibility is
        // driven by maxHeight — leaving `hidden` on would keep the panel
        // display:none and it would never open.
        body.removeAttribute('hidden');
        body.style.overflow = 'hidden';
        if (!expanded) {
          body.style.maxHeight = '0px';
        } else {
          body.style.maxHeight = body.scrollHeight + 'px';
        }
      }
    });
  }

  _onClick(e) {
    const trigger = e.target.closest('[data-accordion-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    const bodyId   = trigger.getAttribute('aria-controls');
    const body     = bodyId ? document.getElementById(bodyId) : trigger.nextElementSibling;

    if (this._exclusive && !expanded) {
      // Close all other items first
      this.querySelectorAll('[data-accordion-trigger][aria-expanded="true"]').forEach(other => {
        if (other === trigger) return;
        this._close(other);
      });
    }

    if (expanded) {
      this._close(trigger);
    } else {
      this._open(trigger, body);
    }
  }

  _open(trigger, body) {
    if (!body) return;
    trigger.setAttribute('aria-expanded', 'true');
    body.style.overflow = 'hidden';
    body.style.maxHeight = body.scrollHeight + 'px';
    body.addEventListener('transitionend', () => {
      // Allow content to grow (e.g. nested accordions) after animation
      body.style.maxHeight = 'none';
      body.style.overflow  = '';
    }, { once: true });
  }

  _close(trigger) {
    const bodyId = trigger.getAttribute('aria-controls');
    const body   = bodyId ? document.getElementById(bodyId) : trigger.nextElementSibling;
    if (!body) return;
    trigger.setAttribute('aria-expanded', 'false');
    // Snap to current height first (needed if maxHeight is 'none')
    body.style.maxHeight = body.scrollHeight + 'px';
    body.style.overflow  = 'hidden';
    // Force reflow before collapsing
    body.getBoundingClientRect();
    body.style.maxHeight = '0px';
  }
}

// =============================================================================
// Custom element definitions
// =============================================================================

definePdpCustomElement('product-main', ProductMain);
definePdpCustomElement('product-gallery', ProductGallery);
definePdpCustomElement('sticky-atc-bar', StickyATCBar);
definePdpCustomElement('product-accordion', ProductAccordion);

// =============================================================================
// ProductMain enhancements — variant URL & _updateVariantURL
// =============================================================================

// Patch _applyVariant to call _updateVariantURL after replaceState
(function () {
  const _origApply = ProductMain.prototype._applyVariant;
  ProductMain.prototype._applyVariant = function (variant, updateHistory) {
    _origApply.call(this, variant, updateHistory);
    // _applyVariant already calls replaceState when updateHistory is true,
    // but we add a dedicated method for external callers and clarity.
    if (updateHistory) this._updateVariantURL(variant);
  };

  ProductMain.prototype._updateVariantURL = function (variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variantId: variant.id }, '', url.toString());
  };
})();

// =============================================================================
// ProductGallery enhancements — autoplay & variant image filtering
// =============================================================================

(function () {
  // Patch connectedCallback to initialise new features after base setup
  const _origConnected    = ProductGallery.prototype.connectedCallback;
  const _origDisconnected = ProductGallery.prototype.disconnectedCallback;

  ProductGallery.prototype.connectedCallback = function () {
    _origConnected.call(this);
    this._initAutoplay();
    this._initVariantFilter();
  };

  ProductGallery.prototype.disconnectedCallback = function () {
    _origDisconnected.call(this);
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }
    if (this._boundVariantFilter) {
      document.removeEventListener('product:variant-changed', this._boundVariantFilter);
    }
  };

  /**
   * Autoplay — cycles through media items on an interval.
   * Activated when data-autoplay-interval (seconds) is present.
   */
  ProductGallery.prototype._initAutoplay = function () {
    const intervalSec = parseInt(this.dataset.autoplayInterval, 10);
    if (!intervalSec || intervalSec <= 0) return;

    this._autoplayTimer = setInterval(() => {
      if (!this._mediaCount) return;
      const next = (this._activeIndex + 1) % this._mediaCount;
      this.setActive(next);
    }, intervalSec * 1000);
  };

  /**
   * Variant image filtering — shows only slides whose data-media-alt-ids
   * includes the newly selected variant's featured_image.id.
   * Activated when data-filter-variant-media attribute is present on the element.
   */
  ProductGallery.prototype._initVariantFilter = function () {
    if (!this.hasAttribute('data-filter-variant-media')) return;

    this._boundVariantFilter = this._onVariantChangedFilter.bind(this);
    document.addEventListener('product:variant-changed', this._boundVariantFilter);
  };

  ProductGallery.prototype._onVariantChangedFilter = function (e) {
    const variant = e.detail?.variant;
    if (!variant) return;

    const featuredId = variant.featured_image?.id;

    if (!featuredId) {
      // No featured image — show all slides
      this._setFilteredSlides(null);
      return;
    }

    const matchingIndices = [];
    this._mediaItems.forEach((item, i) => {
      try {
        const ids = JSON.parse(item.dataset.mediaAltIds || '[]');
        if (ids.includes(featuredId)) matchingIndices.push(i);
      } catch {
        // Malformed JSON — skip
      }
    });

    if (matchingIndices.length === 0) {
      // No matches — show all slides
      this._setFilteredSlides(null);
    } else {
      this._setFilteredSlides(matchingIndices);
    }
  };

  /**
   * Show only slides at the given indices (or all if indices is null).
   * Jumps to the first visible slide.
   */
  ProductGallery.prototype._setFilteredSlides = function (indices) {
    const showAll = indices === null;

    this._mediaItems.forEach((item, i) => {
      const visible = showAll || indices.includes(i);
      item.hidden = !visible;
    });

    this._thumbItems.forEach((thumb, i) => {
      // An editorial tile still behind "View all images" stays hidden even when
      // the filter clears, so restoring all media doesn't spill the whole grid.
      const held = thumb.hasAttribute('data-editorial-extra');
      const visible = (showAll || indices.includes(i)) && !held;
      thumb.hidden = !visible;
    });

    // Jump to first visible slide
    const firstVisible = showAll ? 0 : (indices[0] ?? 0);
    if (this._activeIndex !== firstVisible) {
      this.setActive(firstVisible, false);
    }
  };
})();

// =============================================================================
// ProductTabs
// =============================================================================

class ProductTabs extends HTMLElement {
  connectedCallback() {
    this._buttons = Array.from(this.querySelectorAll('[data-tab-btn]'));
    this._panels  = Array.from(this.querySelectorAll('[data-tab-panel]'));

    if (!this._buttons.length) return;

    // Show panel matching the initially active button, hide the rest
    const initialActive = this._buttons.findIndex(b => b.classList.contains('is-active'));
    this._activate(initialActive >= 0 ? initialActive : 0, false);

    this.addEventListener('click', this._onClick.bind(this));
    this.addEventListener('keydown', this._onKeydown.bind(this));
  }

  _onClick(e) {
    const btn = e.target.closest('[data-tab-btn]');
    if (!btn || !this.contains(btn)) return;
    const index = this._buttons.indexOf(btn);
    if (index >= 0) this._activate(index);
  }

  _onKeydown(e) {
    const btn = e.target.closest('[data-tab-btn]');
    if (!btn || !this.contains(btn)) return;
    const index = this._buttons.indexOf(btn);
    if (index < 0) return;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (index + 1) % this._buttons.length;
      this._activate(next);
      this._buttons[next].focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (index - 1 + this._buttons.length) % this._buttons.length;
      this._activate(prev);
      this._buttons[prev].focus();
    }
  }

  _activate(index, announce = true) {
    this._buttons.forEach((btn, i) => {
      const isActive = i === index;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.tabIndex = isActive ? 0 : -1;
    });

    // Match panels by data-tab-panel index value matching data-tab-btn value
    const targetKey = this._buttons[index]?.dataset.tabBtn ?? String(index);

    this._panels.forEach(panel => {
      const panelKey = panel.dataset.tabPanel;
      const isActive = panelKey === targetKey;
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }
}

definePdpCustomElement('product-tabs', ProductTabs);

// =============================================================================
// DeliveryCountdown — moved to assets/delivery-countdown.js, loaded by the
// sections that render a <delivery-countdown> / <countdown-timer>.
// =============================================================================

// =============================================================================
// CouponCopy — delegated event (not a custom element)
// =============================================================================

document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-coupon-copy]');
    if (!btn) return;

    // Find sibling [data-coupon-code]
    const parent = btn.closest('[data-coupon-code]')
      ? btn.parentElement
      : btn.parentElement;

    const codeEl = parent?.querySelector('[data-coupon-code]')
      || btn.previousElementSibling?.matches?.('[data-coupon-code]') && btn.previousElementSibling
      || btn.nextElementSibling?.matches?.('[data-coupon-code]') && btn.nextElementSibling
      || btn.closest('[data-coupon]')?.querySelector('[data-coupon-code]');

    if (!codeEl) return;

    const text = codeEl.textContent.trim();
    if (!text) return;

    // Store original label
    const origText = btn.textContent;

    navigator.clipboard.writeText(text).then(() => {
      btn.classList.add('is-copied');
      btn.textContent = 'Copied!';
      setTimeout(() => {
        btn.classList.remove('is-copied');
        btn.textContent = origText;
      }, 2000);
    }).catch(() => {
      // Fallback for browsers that block clipboard
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0;';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);

        btn.classList.add('is-copied');
        btn.textContent = 'Copied!';
        setTimeout(() => {
          btn.classList.remove('is-copied');
          btn.textContent = origText;
        }, 2000);
      } catch { /* silent fail */ }
    });
  });
});

// =============================================================================
// InventoryStatus
// =============================================================================

class InventoryStatus extends HTMLElement {
  connectedCallback() {
    this._threshold  = parseInt(this.dataset.threshold, 10) || 5;
    this._showCount  = this.dataset.showCount !== 'false';

    this._dotEl   = this.querySelector('[data-inv-dot]');
    this._labelEl = this.querySelector('[data-inv-label]');
    this._countEl = this.querySelector('[data-inv-count]');
    this._barEl = this.querySelector('[data-inv-bar]');
    this._urgencyEl = this.querySelector('[data-inv-urgency]');

    this._boundVariantChanged = this._onVariantChanged.bind(this);
    document.addEventListener('product:variant-changed', this._boundVariantChanged);

    // Attempt initial render from inline JSON (optional)
    const jsonEl = document.querySelector('script[type="application/json"][data-variant-inventory]');
    if (jsonEl) {
      try {
        const variant = JSON.parse(jsonEl.textContent);
        this._applyInventory(variant);
      } catch { /* skip */ }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('product:variant-changed', this._boundVariantChanged);
  }

  _onVariantChanged(e) {
    const variant = e.detail?.variant;
    if (!variant) return;
    this._applyInventory(variant);
  }

  _applyInventory(variant) {
    // Hide entirely when Shopify isn't tracking inventory
    if (!variant.inventory_management) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    const qty          = variant.inventory_quantity ?? 0;
    const continueOnOOS = variant.inventory_policy === 'continue';
    const available    = variant.available;

    let state;
    if (!available && !continueOnOOS) {
      state = 'out-of-stock';
    } else if (qty > 0 && qty <= this._threshold && !continueOnOOS) {
      // Only "low stock" when the quantity is actually known to be low. The
      // storefront product JSON usually omits inventory_quantity (it defaults
      // to 0 here), so an available variant with unknown qty is treated as
      // in-stock rather than falsely flagged low.
      state = 'low-stock';
    } else {
      state = 'in-stock';
    }

    // Update dot classes
    if (this._dotEl) {
      this._dotEl.classList.remove('pdp__inv-dot--in', 'pdp__inv-dot--low', 'pdp__inv-dot--out');
      if (state === 'in-stock')    this._dotEl.classList.add('pdp__inv-dot--in');
      if (state === 'low-stock')   this._dotEl.classList.add('pdp__inv-dot--low');
      if (state === 'out-of-stock') this._dotEl.classList.add('pdp__inv-dot--out');
    }
    this.dataset.invState = state;

    // Update label
    if (this._labelEl) {
      const labels = {
        'in-stock':    this.dataset.labelInStock    || 'In stock',
        'low-stock':   this.dataset.labelLowStock   || 'Low stock',
        'out-of-stock': this.dataset.labelOutOfStock || 'Out of stock',
      };
      this._labelEl.textContent = labels[state];
    }

    // Update count
    if (this._countEl) {
      if (this._showCount && state !== 'out-of-stock') {
        this._countEl.textContent = qty > 0 ? `${qty} left` : '';
        this._countEl.hidden = qty <= 0;
      } else {
        this._countEl.textContent = '';
        this._countEl.hidden = true;
      }
    }

    if (this._barEl) {
      const width = state === 'out-of-stock'
        ? 0
        : Math.min(100, Math.max(8, Math.round((qty / this._threshold) * 100)));
      this._barEl.style.width = `${width}%`;
    }

    if (this._urgencyEl) {
      if (state === 'low-stock') {
        this._urgencyEl.textContent = qty > 0
          ? (window.theme?.strings?.product?.colorLowStockCount || 'Going fast. Only [count] left in this colour.').replace('[count]', qty)
          : (window.theme?.strings?.product?.colorLowStock || 'Going fast. Order soon to secure this colour.');
      } else if (state === 'out-of-stock') {
        this._urgencyEl.textContent = window.theme?.strings?.product?.colorSoldOut || 'This colour is currently sold out.';
      } else {
        this._urgencyEl.textContent = window.theme?.strings?.product?.colorPopular || 'Popular colour. Stock updates live.';
      }
    }
  }
}

definePdpCustomElement('inventory-status', InventoryStatus);

// Product-card stars link to #reviews (not the widget id) so the browser doesn't jump; glide there instead.
if (location.hash === '#reviews') {
  window.addEventListener('load', () => {
    document.getElementById('judgeme_product_reviews')?.scrollIntoView({ behavior: 'smooth' });
  });
}
